import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaCopy, FaRegCheckCircle, FaEnvelopeOpenText, 
  FaPlay, FaCheck, FaServer, FaEnvelope, FaGlobe, FaShieldAlt, 
  FaLock, FaCheckCircle, FaExclamationTriangle, FaNetworkWired
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function EmailDetail() {
  useTheme();

  // 1. Interactive Domain Authenticator settings
  const [domainName, setDomainName] = useState('acme-enterprise.com');
  const [copiedSpec, setCopiedSpec] = useState(false);

  // Authenticator flags
  const [mxConnected, setMxConnected] = useState(true);
  const [spfConfigured, setSpfConfigured] = useState(true);
  const [dkimApproved, setDkimApproved] = useState(false);
  const [dmarcPolicy, setDmarcPolicy] = useState('quarantine'); // none, quarantine, reject

  // Verification status logs
  const [authenticatorLogs, setAuthenticatorLogs] = useState([
    'SYSTEM: Domain port initialized. Awaiting DNS health checks...'
  ]);

  const triggerLog = (msg) => {
    setAuthenticatorLogs(prev => [msg, ...prev.slice(0, 7)]);
  };

  // Change domain trigger log
  useEffect(() => {
    triggerLog(`SYSTEM: Domain registry targets re-routed to [${domainName || 'yourdomain.com'}]`);
  }, [domainName]);

  // Record changes logging
  useEffect(() => {
    if (mxConnected) triggerLog('[dns.mx] Registered record ➜ O365 core: "acme-enterprise-com.mail.protection.outlook.com"');
  }, [mxConnected]);

  useEffect(() => {
    if (spfConfigured) triggerLog('[dns.spf] Registered record ➜ spf.protection: "v=spf1 include:spf.protection.outlook.com -all"');
  }, [spfConfigured]);

  useEffect(() => {
    triggerLog(`[dns.dmarc] Policy changed ➜ action: "${dmarcPolicy.toUpperCase()}"`);
  }, [dmarcPolicy]);

  // 2. Interactive DNS health scan execution
  const [scanningDns, setScanningDns] = useState(false);
  const [scanCompleted, setScanCompleted] = useState(false);
  const [scanOutput, setScanOutput] = useState('');

  const handleRunDnsScan = () => {
    if (scanningDns) return;
    setScanningDns(true);
    setScanCompleted(false);
    setScanOutput('$ dns-audit --verify-domain ' + (domainName || 'yourdomain.com') + '\n');

    setTimeout(() => {
      setScanOutput(prev => prev + '> Resolving MX hosts... FOUND (Microsoft Outlook Exchange Secure Service)\n');
    }, 500);

    setTimeout(() => {
      setScanOutput(prev => prev + '> Parsing SPF policies... v=spf1 configured WITH include:spf.protection.outlook.com\n');
    }, 1000);

    setTimeout(() => {
      setScanOutput(prev => prev + (dkimApproved 
        ? '> Validating DKIM Signature... ACTIVE (Cryptographic domain keys verified)\n'
        : '> Validating DKIM Signature... WARNING (No DKIM selector found. Inboxes might experience spam flags)\n'
      ));
    }, 1500);

    setTimeout(() => {
      setScanOutput(prev => prev + `> Testing DMARC Policy... OK (Policy set to: ${dmarcPolicy.toUpperCase()})\n`);
    }, 2000);

    setTimeout(() => {
      setScanOutput(prev => prev + '✔ Validation results: 100% VERIFIED BY OFFICIAL MICROSOFT SUITES INTERACTION.');
      setScanningDns(false);
      setScanCompleted(true);
      triggerLog(`SYSTEM: DNS verification audit passed cleanly for domain [${domainName || 'yourdomain.com'}]`);
    }, 2600);
  };

  const resetDnsScan = () => {
    setScanCompleted(false);
    setScanOutput('');
  };

  const handleCopySpec = () => {
    const specSummary = `BCC Email Setup specs:
- Corporate Domain: ${domainName || 'yourdomain.com'}
- Microsoft Partner MX Setup: ${mxConnected ? 'Enabled' : 'Disabled'}
- SPF Rules Map: ${spfConfigured ? 'Outlook Protection SPF' : 'None'}
- DKIM Keys Status: ${dkimApproved ? 'Digital Keys Approved' : 'No Keys'}
- DMARC Policy Level: ${dmarcPolicy.toUpperCase()}`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  // 3. Migration Milestones Interactive Timeline
  const [migrationStage, setMigrationStage] = useState('discovery');

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden animate-fade-in" 
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)' 
               }}>
        <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-orange-500" />
        
        <div className="section-container relative z-10 text-center max-w-4xl">
          
          {/* Microsoft Vendor Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border border-orange-505/20 bg-orange-500/10 shadow-sm"
               style={{ borderColor: 'var(--border-color)' }}>
            <span className="w-2.5 h-2.5 rounded bg-clip-border flex items-center justify-center shrink-0">
               <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-ping" />
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-orange-500">Official Microsoft Certified Partner</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Corporate <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-rose-400">Email Hosting</span>.
          </h1>
          
          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We deploy secure, enterprise-grade SMTP routing and global Office 365 Exchange environments. As Microsoft Certified Partners, we coordinate full zero-loss internal migration and strict spam-protection DNS map configurations.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary inline-flex bg-gradient-to-r from-orange-500 to-rose-500 text-white border-none hover:opacity-90">
              Start Email Migration Spec <FaArrowRight size={11} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PARTNERSHIP ADVANTAGES & CAPABILITIES ── */}
      <section className="py-24 px-6 border-b animate-fade-in" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Partner Benefits</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Microsoft Exchange Integration</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Professional management from setting up SPF, DKIM, and DMARC security parameters to backing up legacy inbox folders.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1: MS Partnership */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="p-8 rounded-3xl border bg-gradient-to-br from-orange-550/10 to-rose-500/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-400 mb-6">
                  <FaServer size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Certified Microsoft Cloud Vendor</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Benefit from certified setup architectures. We provision corporate Outlook suites, shared team schedules, global admin access rights, and dedicated cloud databases.
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-orange-400 mb-4">
                  <span className="w-4 h-px bg-orange-400 inline-block" /> Cloud Vendor
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Certified MS Provisioning
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We are credentialed Microsoft Cloud Providers, ensuring your company obtains robust licensing packages, isolated Exchange configurations, and direct service escalations.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-orange-500 mt-1 shrink-0" />
                    Direct global corporate licensing setups for O365/Outlook.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-orange-500 mt-1 shrink-0" />
                    Full administrative dashboard setup and security config.
                  </li>
                </ul>
              </div>
            </div>

            {/* Row 2: Secure Deliverability */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-rose-400 mb-4">
                  <span className="w-4 h-px bg-rose-400 inline-block" /> Security Gate
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  DKIM, SPF & DMARC Deliverability
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Avoid the spam folder. We configure proper cryptographic domain keys (DKIM), Sender Policy Frameworks (SPF), and DMARC enforcement policies so your company mails land directly in user inboxes.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-rose-500 mt-1 shrink-0" />
                    Complete anti-spoofing block rules definition.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-rose-500 mt-1 shrink-0" />
                    Prevention codes for host manipulation or security phishing.
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 p-8 rounded-3xl border bg-gradient-to-br from-rose-500/10 to-orange-550/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-450 mb-6">
                  <FaShieldAlt size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Inbox Protection Rules</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Protect against corporate spoofing triggers. Set up spam filter blocklists, isolated quarantine rules, and secure inbox verification loops.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE INTERACTIVE DNS CONSOLE BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Authenticator Preview</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Interactive Domain DNS Console</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Input your target corporate domain, activate security records, and run simulated health checks to verify deliverability status.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Controls (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl"
                 style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                
                {/* Domain Input */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-2.5 text-orange-500">1. Target System Domain</h3>
                <div className="relative mb-6">
                  <input
                    type="text"
                    value={domainName}
                    onChange={(e) => setDomainName(e.target.value)}
                    placeholder="yourcompany.com"
                    className="w-full py-2 px-3 pl-8 text-xs rounded-xl bg-zinc-950 text-white font-mono border border-zinc-800 focus:border-orange-500 outline-none transition-all"
                  />
                  <FaGlobe className="absolute left-3 top-3 text-[10px] text-zinc-500" />
                </div>

                {/* DNS Checkbox controls */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-4 text-orange-500">2. Active Domain Records Configuration</h3>
                <div className="space-y-2.5 mb-6">
                  
                  {/* MX Record */}
                  <button 
                    onClick={() => setMxConnected(!mxConnected)}
                    className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                      mxConnected ? 'bg-orange-500/10 border-orange-500/40 text-orange-400' : 'border-zinc-800/10 dark:border-zinc-700/10'
                    }`}
                  >
                    <div className="w-7 h-7 rounded bg-orange-500/10 flex items-center justify-center text-orange-400 shrink-0">
                      <FaServer size={11} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[10px] font-black uppercase text-white leading-none">Microsoft MX Mapping</h4>
                      <p className="text-[8.5px] text-zinc-500 truncate mt-1">Routes mailboxes directly through outlook.com</p>
                    </div>
                    {mxConnected && <span className="text-[7.5px] font-black px-2 py-0.5 border border-orange-500/35 rounded text-orange-400 uppercase">ACTIVE</span>}
                  </button>

                  {/* SPF Record */}
                  <button 
                    onClick={() => setSpfConfigured(!spfConfigured)}
                    className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                      spfConfigured ? 'bg-orange-500/10 border-orange-500/40 text-orange-400' : 'border-zinc-800/10 dark:border-zinc-700/10'
                    }`}
                  >
                    <div className="w-7 h-7 rounded bg-orange-500/10 flex items-center justify-center text-orange-400 shrink-0">
                      <FaShieldAlt size={11} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[10px] font-black uppercase text-white leading-none">SPF Anti-Spoof Key</h4>
                      <p className="text-[8.5px] text-zinc-500 truncate mt-1">Enforce Outlook IP authorisations</p>
                    </div>
                    {spfConfigured && <span className="text-[7.5px] font-black px-2 py-0.5 border border-orange-500/35 rounded text-orange-400 uppercase">SPF_OK</span>}
                  </button>

                  {/* DKIM Record Toggle */}
                  <button 
                    onClick={() => setDkimApproved(!dkimApproved)}
                    className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                      dkimApproved ? 'bg-orange-500/10 border-orange-500/40 text-orange-400' : 'border-zinc-800/10 dark:border-zinc-700/10'
                    }`}
                  >
                    <div className="w-7 h-7 rounded bg-orange-500/10 flex items-center justify-center text-orange-400 shrink-0">
                      <FaLock size={11} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[10px] font-black uppercase text-white leading-none">Active DKIM Signature</h4>
                      <p className="text-[8.5px] text-zinc-500 truncate mt-1">Cryptographic key encryption verification</p>
                    </div>
                    {dkimApproved ? (
                      <span className="text-[7.5px] font-black px-2 py-0.5 border border-emerald-500/35 rounded text-emerald-400 uppercase">SIGNED</span>
                    ) : (
                      <span className="text-[7.5px] font-black px-2 py-0.5 border border-rose-500/25 rounded text-rose-400 uppercase">MISSING</span>
                    )}
                  </button>

                </div>

                {/* DMARC Selector */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-3.5 text-orange-500">3. Choose DMARC Security Policy</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'none', label: 'Monitor Only' },
                    { id: 'quarantine', label: 'Quarantine' },
                    { id: 'reject', label: 'Strict Reject' }
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setDmarcPolicy(p.id)}
                      className={`py-1.5 rounded-lg text-[8.5px] font-black uppercase tracking-wider text-center transition-all ${
                        dmarcPolicy === p.id 
                          ? 'bg-orange-500 text-white shadow font-black scale-102' 
                          : 'bg-zinc-900 border border-zinc-850 text-zinc-500'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

              </div>

              {/* Action buttons */}
              <div className="mt-8">
                <button
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-gradient-to-r from-orange-500 to-rose-500 hover:opacity-90 text-white font-black text-xs tracking-wider transition-all"
                >
                  {copiedSpec ? <FaRegCheckCircle size={13} /> : <FaCopy size={12} />}
                  {copiedSpec ? "CONFIG SPEC COPIED!" : "COPY SPEC AND CONTACT US"}
                </button>
              </div>

            </div>

            {/* Right Screen Mock (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col justify-between shadow-xl"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                <div>
                  <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse" />
                      <span className="text-[10px] font-mono uppercase font-bold tracking-widest text-zinc-400">
                        Visual Domain DNS Record Mapper
                      </span>
                    </div>
                    <span className="text-[9px] uppercase font-bold border border-orange-500/25 px-2.5 py-0.5 rounded tracking-wide text-orange-400" >
                      MS Exchange Verified
                    </span>
                  </div>

                  {/* Browser Mock Wrapper */}
                  <div className="w-full border rounded-2xl overflow-hidden relative bg-zinc-950/45 border-zinc-850" style={{ backdropFilter: 'blur(10px)' }}>
                    
                    {/* URL Bar */}
                    <div className="bg-zinc-900 px-4 py-2 border-b border-zinc-850 flex items-center justify-between">
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                      </div>
                      
                      <div className="flex-grow max-w-sm mx-auto h-5 bg-zinc-950 rounded text-[9px] text-zinc-500 flex items-center justify-between px-2 font-mono border border-zinc-850">
                        <span>DNS Lookup: {domainName || 'yourdomain.com'}</span>
                        <span className="text-[8px] font-black text-orange-400 uppercase">Live Map</span>
                      </div>

                      <div className="w-4" />
                    </div>

                    {/* Page Content Dashboard */}
                    <div className="p-4 space-y-3.5 text-left h-[260px] overflow-y-auto">
                      
                      {/* DNS records mapping simulation logs */}
                      <span className="text-[7.5px] uppercase font-mono font-black text-orange-400 tracking-widest block border-b border-zinc-850 pb-1.5">DNS Records Registry</span>

                      <div className="space-y-2">
                        {/* MX Output line */}
                        <div className="p-2.5 rounded-lg border border-zinc-850 bg-zinc-900/60 flex items-center justify-between text-[8px] font-mono select-all">
                          <div>
                            <span className="text-zinc-550 mr-1">TYPE: MX</span>
                            <span className="text-white ml-2">{mxConnected ? `${domainName.replace('.com', '')}-com.mail.protection.outlook.com` : 'Record empty'}</span>
                          </div>
                          <span className={mxConnected ? 'text-orange-400 font-bold' : 'text-rose-400 font-bold'}>
                            {mxConnected ? '● CONNECTED' : '● DISCONNECTED'}
                          </span>
                        </div>

                        {/* SPF Output line */}
                        <div className="p-2.5 rounded-lg border border-zinc-850 bg-zinc-900/60 flex items-center justify-between text-[8px] font-mono select-all">
                          <div>
                            <span className="text-zinc-555 mr-1">TYPE: TXT (SPF)</span>
                            <span className="text-white ml-2">{spfConfigured ? 'v=spf1 include:spf.protection.outlook.com -all' : 'Record empty'}</span>
                          </div>
                          <span className={spfConfigured ? 'text-orange-400 font-bold' : 'text-rose-400 font-bold'}>
                            {spfConfigured ? '● VERIFIED' : '● UNRESOLVED'}
                          </span>
                        </div>

                        {/* DMARC Output line */}
                        <div className="p-2.5 rounded-lg border border-zinc-850 bg-zinc-900/60 flex items-center justify-between text-[8px] font-mono select-all">
                          <div>
                            <span className="text-zinc-555 mr-1">TYPE: TXT (DMARC)</span>
                            <span className="text-white ml-2">v=DMARC1; p={dmarcPolicy}; pct=100;</span>
                          </div>
                          <span className="text-emerald-400 font-bold">● POLICED</span>
                        </div>
                      </div>

                      {/* Live webhook logging console */}
                      <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-950 font-mono text-[7px]">
                        <span className="text-[6.5px] uppercase font-bold text-zinc-500 block mb-1.5 tracking-wider border-b border-zinc-850 pb-1">Real-time SMTP Diagnostic Outpost</span>
                        <div className="space-y-1 h-[65px] overflow-y-auto text-zinc-400">
                          {authenticatorLogs.map((log, index) => (
                            <div key={index} className="truncate">
                              <span className="text-zinc-605 mr-1.5">➜</span>
                              <span className={log.includes('SYSTEM:') ? 'text-zinc-500' : 'text-orange-400'}>{log}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>

                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── INTERACTIVE MIGRATION MILESTONES TIMELINE ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-card)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-4xl mx-auto">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Migration Sprints</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Outlook Migration Stages</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our onboarding workflow checkpoints. Click on a stage below to run simulated terminal commands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Milestones selector (5 columns) */}
            <div className="md:col-span-5 space-y-3">
              {[
                { id: 'discovery', step: '01', title: 'Domain Bind', desc: 'Configure MX and TXT domain credentials.' },
                { id: 'identity', step: '02', title: 'Identity Setup', desc: 'Provision user directory profiles and calendars.' },
                { id: 'migration', step: '03', title: 'Inbox Synchronization', desc: 'Securely sync legacy mails into O365 clouds.' },
                { id: 'verifying', step: '04', title: 'DNS Health Checks', desc: 'Pass anti-phishing SPF, DKIM, and DMARC validations.' },
                { id: 'cutover', step: '05', title: 'SMTP Live Cutover', desc: 'Frictionless switchover to Microsoft Exchange.' }
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    setMigrationStage(s.id);
                    resetDnsScan();
                  }}
                  className={`w-full text-left p-3.5 rounded-2xl border flex items-center gap-4 transition-all duration-200 ${
                    migrationStage === s.id 
                      ? 'bg-orange-500/10 border-orange-500/40 text-orange-400 scale-[1.01]' 
                      : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                  }`}
                >
                  <span className={`text-md font-mono font-black ${migrationStage === s.id ? 'text-orange-400' : 'text-zinc-550'}`}>
                    {s.step}
                  </span>
                  <div>
                    <h4 className="text-xs font-black uppercase text-white leading-none mb-1">{s.title}</h4>
                    <p className="text-[9.5px] text-zinc-450">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Simulated diagnostic terminal logs (7 columns) */}
            <div className="md:col-span-7">
              <div className="w-full border rounded-2xl overflow-hidden bg-zinc-950 border-zinc-850 p-4 font-mono shadow-2xl relative">
                
                {/* Core Header */}
                <div className="flex justify-between items-center pb-2.5 border-b border-zinc-850 mb-3.5 text-zinc-500 text-[10px]">
                  <span className="uppercase tracking-widest font-bold">Terminal : gcc_smtp_registry</span>
                  <div className="flex gap-1.5 shrink-0">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/30" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/30" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/30" />
                  </div>
                </div>

                {/* Console text log show */}
                <div className="min-h-[170px] text-left text-zinc-350 text-[10px] space-y-2 leading-relaxed">
                  {scanOutput ? (
                    <pre className="whitespace-pre-wrap font-sans">{scanOutput}</pre>
                  ) : (
                    <div className="text-zinc-500 flex flex-col items-center justify-center min-h-[160px] text-center">
                      <span className="text-2xl mb-2 text-zinc-650">🛰</span>
                      <span>Ready to trigger DNS lookup health audits for [stage: {migrationStage.toUpperCase()}]</span>
                    </div>
                  )}
                </div>

                {/* Submit button */}
                <div className="mt-4 pt-3 border-t border-zinc-850 flex justify-between items-center gap-2">
                  <span className="text-[8px] text-zinc-500">DNS Registry: online</span>
                  <button
                    onClick={handleRunDnsScan}
                    disabled={scanningDns || scanCompleted}
                    className={`py-1.5 px-4 rounded-xl text-[9px] font-black uppercase transition-all tracking-wider ${
                      scanCompleted 
                        ? 'bg-orange-500 text-white font-black hover:opacity-90' 
                        : scanningDns 
                          ? 'bg-zinc-850 text-zinc-555 cursor-not-allowed'
                          : 'bg-zinc-800 text-white hover:bg-zinc-700 active:scale-95'
                    }`}
                  >
                    {scanCompleted ? '✔ Audit Completed' : scanningDns ? '⚡ Verifying...' : '▶ Run Audit'}
                  </button>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── BOTTOM BACK CTA ── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
         <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
               style={{ color: 'var(--accent)' }}>
            <FaArrowRight size={12} className="transform rotate-180 text-orange-450" /> Back to All Services
         </Link>
      </section>

    </div>
  );
}
